import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import Select from "react-select";
import Icon from "@identitybuilding/idb-react-iconlib";
import { updateAccountInfo } from "../../actions/AccountAction";
import { getEnvEndpoints } from "../../utils/functions";

const ProfileUpdateForm = ({ createNotification }) => {
    const dispatch = useDispatch();
    const { translate, lang } = useSelector((state) => state.general);
    const { accountData } = useSelector((state) => state.account);
    const env = getEnvEndpoints();

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [telephone, setTelephone] = useState("");
    const [vatNumber, setVatNumber] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [street, setStreet] = useState("");
    const [houseNumber, setHouseNumber] = useState("");
    const [box, setBox] = useState("");
    const [municipalityName, setMunicipalityName] = useState("");
    const [subMunicipalityOptions, setSubMunicipalityOptions] = useState([]);
    const [selectedSubMunicipality, setSelectedSubMunicipality] = useState(null);
    const [addressAutoFilled, setAddressAutoFilled] = useState(false);
    const [profileImage, setProfileImage] = useState(null);
    const [profileImageName, setProfileImageName] = useState("");
    const [loading, setLoading] = useState(false);

    const postalCodeTimerRef = useRef(null);
    const didInitialLookupRef = useRef(false);
    const fileInputRef = useRef(null);

    const triggerFileUpload = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    useEffect(() => {
        if (accountData) {
            setFirstName(accountData.first_name || "");
            setLastName(accountData.last_name || "");
            setTelephone(accountData.telephone || "");
            setVatNumber(accountData.vat_number || "");
            setPostalCode(accountData.postal_code || "");
            setStreet(accountData.street || "");
            setHouseNumber(accountData.house_number || "");
            setBox(accountData.box || "");
            if (accountData.image) {
                const imageName = accountData.image.split('/').pop();
                setProfileImageName(imageName || "");
            }
        }
    }, [accountData]);

    // On initial load, if the user already has a postal code, run the lookup once
    // so the municipality/sub-municipality dropdowns populate correctly.
    useEffect(() => {
        if (didInitialLookupRef.current) return;
        if (!accountData || !accountData.postal_code) return;
        if (accountData.postal_code.length !== 4) return;
        didInitialLookupRef.current = true;
        runAddressLookup(accountData.postal_code, accountData.sub_municipality);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountData]);

    const runAddressLookup = async (pc, prefillSubMunicipalityName) => {
        try {
            const response = await axios.get(
                `${env.REACT_APP_BASE_MEDIA_URL}/geo/api/address-lookup/?postal_code=${pc}&lang=${lang}`
            );
            const data = response.data;

            if (data.municipality) {
                setMunicipalityName(data.municipality.name);
                setAddressAutoFilled(true);
            } else {
                setAddressAutoFilled(false);
                setMunicipalityName("");
            }

            if (data.sub_municipalities && data.sub_municipalities.length > 0) {
                const options = data.sub_municipalities.map((sm) => ({
                    value: sm.id,
                    label: sm.name,
                }));
                setSubMunicipalityOptions(options);

                // Try to match the user's saved sub_municipality name
                if (prefillSubMunicipalityName) {
                    const match = options.find((o) => o.label === prefillSubMunicipalityName);
                    if (match) {
                        setSelectedSubMunicipality(match);
                        return;
                    }
                }
                if (data.sub_municipalities.length === 1) {
                    setSelectedSubMunicipality(options[0]);
                } else {
                    setSelectedSubMunicipality(null);
                }
            } else {
                setSubMunicipalityOptions([]);
                setSelectedSubMunicipality(null);
            }
        } catch (err) {
            console.error("Address lookup failed:", err);
            setAddressAutoFilled(false);
        }
    };

    const handlePostalCodeChange = (value) => {
        setPostalCode(value);
        if (postalCodeTimerRef.current) clearTimeout(postalCodeTimerRef.current);

        if (!value || value.length < 4) {
            setAddressAutoFilled(false);
            setMunicipalityName("");
            setSubMunicipalityOptions([]);
            setSelectedSubMunicipality(null);
            return;
        }
        if (value.length === 4 && /^\d{4}$/.test(value)) {
            postalCodeTimerRef.current = setTimeout(() => {
                runAddressLookup(value);
            }, 400);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(file);
            setProfileImageName(file.name);
        }
    };

    const isCompany = accountData?.registration_type === 'company';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!firstName.trim()) {
            createNotification("warning", translate("please_enter_first_name") || "Please enter your first name");
            return;
        }
        if (!lastName.trim()) {
            createNotification("warning", translate("please_enter_last_name") || "Please enter your last name");
            return;
        }
        if (isCompany) {
            if (!vatNumber || !vatNumber.trim()) {
                createNotification("warning", translate("vat_number_required") || "BTW-nummer is vereist");
                return;
            }
            if (!/^BE\d{10}$/.test(vatNumber.trim().replace(/[.\s]/g, ''))) {
                createNotification("warning", translate("vat_number_invalid") || "Ongeldig BTW-nummer");
                return;
            }
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("first_name", firstName);
            formData.append("last_name", lastName);
            formData.append("telephone", telephone);
            if (isCompany) {
                formData.append("vat_number", vatNumber);
            }
            formData.append("postal_code", postalCode);
            formData.append("street", street);
            formData.append("house_number", houseNumber);
            formData.append("box", box);
            formData.append("sub_municipality", selectedSubMunicipality?.label || "");
            if (profileImage) {
                formData.append("image", profileImage);
            }

            const headers = {
                "Content-Type": "multipart/form-data",
                Authorization: `Token ${localStorage.getItem("StadGemeenteToken")}`,
            };

            await axios.patch(
                `${env.REACT_APP_BASE_MEDIA_URL}/core/api/stadgemeente/profile/`,
                formData,
                { headers }
            );

            createNotification("success", translate("profile_updated") || "Profile updated successfully");

            const res = await axios.get(
                `${env.REACT_APP_BASE_MEDIA_URL}/core/api/stadgemeente/user/?lang=${lang}`,
                { headers: { Authorization: `Token ${localStorage.getItem("StadGemeenteToken")}` } }
            );
            dispatch(updateAccountInfo(res.data));
        } catch (error) {
            console.error("Profile update error:", error);
            createNotification("error", translate("profile_update_failed") || "Failed to update profile");
        }
        setLoading(false);
    };

    return (
        <section className="settings-section">
            <h2>{translate("update_profile") || "Profiel bijwerken"}</h2>
            <p className="section-description">{translate("profile_section_description") || "Werk uw persoonlijke informatie bij."}</p>
            <form onSubmit={handleSubmit} className="profile-form">
                {/* First Name & Last Name */}
                <div className="form-row">
                    <div className="float-input-field">
                        <div className="input-group">
                            <input
                                id="first_name"
                                name="first_name"
                                type="text"
                                placeholder={translate("first_name") || "Voornaam"}
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="form-field"
                                autoComplete="off"
                            />
                            <label htmlFor="first_name" className="input-label">
                                {translate("first_name") || "Voornaam"}
                            </label>
                        </div>
                    </div>
                    <div className="float-input-field">
                        <div className="input-group">
                            <input
                                id="last_name"
                                name="last_name"
                                type="text"
                                placeholder={translate("last_name") || "Achternaam"}
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="form-field"
                                autoComplete="off"
                            />
                            <label htmlFor="last_name" className="input-label">
                                {translate("last_name") || "Achternaam"}
                            </label>
                        </div>
                    </div>
                </div>

                {/* Email (Read-only) */}
                <div className="float-input-field">
                    <div className="input-group">
                        <input
                            id="email"
                            name="email"
                            type="email"
                            placeholder={translate("email") || "E-mail"}
                            value={accountData?.email || ""}
                            className="form-field disabled"
                            disabled
                            autoComplete="off"
                        />
                        <label htmlFor="email" className="input-label">
                            {translate("email") || "E-mail"}
                        </label>
                    </div>
                    <span className="readonly-label">{translate("cannot_change_email") || "E-mail kan niet worden gewijzigd"}</span>
                </div>

                {/* Telephone */}
                <div className="float-input-field">
                    <div className="input-group">
                        <input
                            id="telephone"
                            name="telephone"
                            type="tel"
                            placeholder={translate("phone") || "Telefoon"}
                            value={telephone}
                            onChange={(e) => setTelephone(e.target.value)}
                            className="form-field"
                            autoComplete="off"
                            maxLength={14}
                        />
                        <label htmlFor="telephone" className="input-label">
                            {translate("phone") || "Telefoon"}
                        </label>
                    </div>
                </div>

                {/* VAT number (only for company accounts, shown before the address section) */}
                {isCompany && (
                    <div className="float-input-field">
                        <div className="input-group">
                            <input
                                id="vat_number"
                                name="vat_number"
                                type="text"
                                placeholder={translate("vat_number") || "BTW-nummer"}
                                value={vatNumber}
                                onChange={(e) => setVatNumber(e.target.value)}
                                className="form-field"
                                autoComplete="off"
                            />
                            <label htmlFor="vat_number" className="input-label">
                                {translate("vat_number") || "BTW-nummer"}*
                            </label>
                        </div>
                    </div>
                )}

                {/* Postal code + Municipality (auto-filled from postal code lookup) */}
                <div className="form-row form-row--address">
                    <div className="float-input-field">
                        <div className="input-group">
                            <input
                                id="postal_code"
                                name="postal_code"
                                type="text"
                                placeholder={translate("postal_code") || "Postcode"}
                                value={postalCode}
                                onChange={(e) => handlePostalCodeChange(e.target.value)}
                                className="form-field"
                                autoComplete="off"
                                maxLength={4}
                            />
                            <label htmlFor="postal_code" className="input-label">
                                {translate("postal_code") || "Postcode"}
                            </label>
                        </div>
                    </div>
                    <div className="float-input-field">
                        <div className="input-group">
                            <input
                                id="municipality_name"
                                name="municipality_name"
                                type="text"
                                placeholder={translate("mi_municipality") || "Stad/Gemeente"}
                                value={municipalityName}
                                readOnly
                                className="form-field disabled"
                                autoComplete="off"
                            />
                            <label htmlFor="municipality_name" className="input-label">
                                {translate("mi_municipality") || "Stad/Gemeente"}
                            </label>
                        </div>
                    </div>
                </div>

                {/* Sub-municipality (dropdown if multiple, read-only if one) */}
                {subMunicipalityOptions.length > 1 ? (
                    <div className="float-input-field">
                        <label className="input-label" style={{ position: 'static', marginBottom: 4, color: '#666', fontSize: 13 }}>
                            {translate("ci_sub_municipality") || "Deelgemeente"}
                        </label>
                        <Select
                            value={selectedSubMunicipality}
                            options={subMunicipalityOptions}
                            onChange={(option) => setSelectedSubMunicipality(option)}
                            placeholder={translate("ci_sub_municipality") || "Deelgemeente"}
                            isClearable
                            classNamePrefix="profile-sub-muni-select"
                        />
                    </div>
                ) : (
                    <div className="float-input-field">
                        <div className="input-group">
                            <input
                                id="sub_municipality"
                                name="sub_municipality"
                                type="text"
                                placeholder={translate("ci_sub_municipality") || "Deelgemeente"}
                                value={selectedSubMunicipality?.label || ""}
                                readOnly
                                className="form-field disabled"
                                autoComplete="off"
                            />
                            <label htmlFor="sub_municipality" className="input-label">
                                {translate("ci_sub_municipality") || "Deelgemeente"}
                            </label>
                        </div>
                    </div>
                )}

                {/* Street (full width) */}
                <div className="float-input-field">
                    <div className="input-group">
                        <input
                            id="street"
                            name="street"
                            type="text"
                            placeholder={translate("mi_street") || "Straat"}
                            value={street}
                            onChange={(e) => setStreet(e.target.value)}
                            className="form-field"
                            autoComplete="off"
                        />
                        <label htmlFor="street" className="input-label">
                            {translate("mi_street") || "Straat"}
                        </label>
                    </div>
                </div>

                {/* House number + Bus */}
                <div className="form-row">
                    <div className="float-input-field">
                        <div className="input-group">
                            <input
                                id="house_number"
                                name="house_number"
                                type="text"
                                placeholder={translate("house_number") || "Huisnummer"}
                                value={houseNumber}
                                onChange={(e) => setHouseNumber(e.target.value)}
                                className="form-field"
                                autoComplete="off"
                            />
                            <label htmlFor="house_number" className="input-label">
                                {translate("house_number") || "Huisnummer"}
                            </label>
                        </div>
                    </div>
                    <div className="float-input-field">
                        <div className="input-group">
                            <input
                                id="box"
                                name="box"
                                type="text"
                                placeholder={translate("box_no") || "Bus"}
                                value={box}
                                onChange={(e) => setBox(e.target.value)}
                                className="form-field"
                                autoComplete="off"
                            />
                            <label htmlFor="box" className="input-label">
                                {translate("box_no") || "Bus"}
                            </label>
                        </div>
                    </div>
                </div>

                {/* Profile Image — the whole field is clickable to open the file picker */}
                <div
                    className="float-input-field file-input-field"
                    onClick={triggerFileUpload}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            triggerFileUpload();
                        }
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="input-group">
                        <input
                            id="profile_image"
                            name="profile_image"
                            type="text"
                            placeholder={translate("profile_image") || "Profielfoto"}
                            value={profileImageName}
                            className="form-field file-display"
                            readOnly
                            style={{ cursor: 'pointer' }}
                        />
                        <label htmlFor="profile_image" className="input-label" style={{ cursor: 'pointer' }}>
                            {translate("profile_image") || "Profielfoto"}
                        </label>
                        <span className="file-upload-icon" style={{ cursor: 'pointer' }}>
                            <Icon name="Upload" />
                        </span>
                        <input
                            ref={fileInputRef}
                            id="profile_image_upload"
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="hidden-file-input"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>

                {/* Submit */}
                <div className="form-button">
                    <button type="submit" className="submit-button" disabled={loading}>
                        {loading ? (translate("updating") || "Bijwerken...") : (translate("update_profile") || "Profiel bijwerken")}
                    </button>
                </div>
            </form>
        </section>
    );
};

export default ProfileUpdateForm;
